/**
 * Onboarding success — "You're live, motek 🎉".
 *
 * Locked Motek leak (one per flow) + 🎉 emoji per the 2026-05-22 emoji
 * policy expansion (completion moment).
 *
 * Reveals the Frame H promises as a celebratory beat:
 *   - Every WhatsApp contact matched to the ad that brought them
 *   - Conversions fire to Google Ads and Meta Ads automatically
 *   - Your customer list, yours to export
 *   - ROAS climbs every month
 *
 * Reached by clicking Done on /onboarding/go-live after WhatsApp pairing.
 * Not gated by RequireClient; the explicit navigation is the trust signal.
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/useAuth';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';

const PROMISES = [
  'Every WhatsApp contact matched to the ad that brought them',
  'Conversions fire to Google Ads and Meta Ads automatically',
  'Your customer list, yours to export',
  'ROAS climbs every month',
];

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-green-700">
    <path
      fillRule="evenodd"
      d="M16.704 5.293a1 1 0 0 1 .003 1.414l-7.5 7.55a1 1 0 0 1-1.42.003L3.29 9.797a1 1 0 1 1 1.42-1.41l3.79 3.82 6.79-6.835a1 1 0 0 1 1.414-.079Z"
      clipRule="evenodd"
    />
  </svg>
);

export function OnboardingSuccessPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  return (
    <OnboardingShell
      current="success"
      title="You're live, motek 🎉"
      subtitle="Setup is done. HeyMotek is now matching every WhatsApp contact to the ad that brought them, automatically."
      state={{
        accountCreated: true,
        profileDone: !!me?.memberships?.length,
        snippetDone: !!me?.snippet_verified_at,
        adsDone: !!me?.google_ads_customer_id || !!me?.facebook_pixel_id,
        whatsappDone: !!me?.waba_id,
        isSuccess: true,
      }}
    >
      <div className="space-y-6">
        {/* Frame H promise reveal — the payoff */}
        <div className="rounded-3xl bg-card shadow-card p-7 sm:p-9">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Here's what's now true for you
          </p>
          <ul className="mt-5 space-y-4">
            {PROMISES.map((text) => (
              <li key={text} className="flex items-start gap-3.5">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon />
                </span>
                <span className="text-base text-dark font-medium leading-snug">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex w-full items-center justify-center rounded-full bg-brand px-6 py-4 text-lg font-bold text-white hover:bg-brand-dark transition-colors"
        >
          Open your dashboard
        </button>
      </div>
    </OnboardingShell>
  );
}
